import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import { TriagemSolarModal } from '@/components/kanban/solar/TriagemSolarModal';
import { TriagemCotacaoModal } from '@/components/kanban/cotacao/TriagemCotacaoModal';
import { TriagemDefesaCreditoModal } from '@/components/kanban/cotacao/TriagemDefesaCreditoModal';
import { TriagemEmInstalacaoModal } from '@/components/kanban/instalacao/TriagemEmInstalacaoModal';
import { TriagemErroSolarModal } from '@/components/kanban/errosolar/TriagemErroSolarModal';
import { TriagemFluxoModal } from '@/components/kanban/fluxo/TriagemFluxoModal';
import { TRIAGENS_FLUXO } from '@/components/kanban/fluxo/triagensFluxoConfig';
import { useUserProfile } from '@/hooks/useUserProfile';
import { rotuloPrazo } from '@/hooks/useSla';
import { cn } from '@/lib/utils';
import { MOTIVOS_PENDENCIA, usePendenciaExterna } from './useRitmoTramitacao';
import { AgendaTramitacao, type ItemAgenda } from './AgendaTramitacao';

/* A TRAVA DA BKO TRAMITAÇÃO (Douglas 24/10/2026, "não quero travar elas, mas se
   elas não fizerem no tempo que tem que ser feito vai travar"): cada pedido tem
   o seu prazo (sla_filas) e a trava só aparece quando algum estoura, só com os
   estourados. Destrava o pedido andar, ou a tratativa onde vale (Manter com a
   justificativa padrão, defesa ou cobrança no crédito, pendência externa no
   Erro Solar). O Inbox continua aberto: lá a trava vira faixa. Quem decide é
   fn_bko_tramitacao_travado; o painel só abre as triagens que já existem.
   Desde 30/10/2026 a trava segue os horários do dia (desenho v5): instalação,
   volta de consulta e Erro Solar, a mais alta pausa a mais baixa. */

type Aberta = 'movel' | 'fixa' | 'validacao' | 'negociacao' | 'reprovado' | 'manual' | 'instalacao' | 'erro_solar' | null;
type Fila = 'erro_solar' | 'instalacao' | 'instalacao_volta' | 'consulta' | 'input' | 'validacao' | 'negociacao' | 'credito';
type PedidoTrava = { pedido_id: string; numero_pedido: number | null; cliente: string | null; coluna?: string | null; batido: boolean; estourou_min?: number };
type Trava = {
  aplica: boolean; travado: boolean; fila?: Fila | null; hora?: string | null; horario?: string | null;
  pedidos_trava?: PedidoTrava[]; travas_hoje?: number; pausada?: Fila | null; agenda?: ItemAgenda[];
};

const FUNCOES = ['BKO_INPUT', 'BKO_TRAMITACAO'];

const TRIAGEM_DA_COLUNA: Record<string, Exclude<Aberta, null>> = {
  'INPUT REALIZADO MOVEL': 'movel',
  'INPUT REALIZADO FIXA': 'fixa',
  'VALIDAÇÃO PENDENTE': 'validacao',
  'CREDITO REPROVADO': 'reprovado',
  'ANALISE MANUAL CREDITO': 'manual',
  'NEGOCIAÇÃO (ERRO DE INPUT)': 'negociacao',
  'EM INSTALAÇÃO': 'instalacao',
  'ERRO SOLAR  INPUT MANUAL': 'erro_solar',
};

const FILAS: Record<Fila, { nome: string; regra: string; destrava: string; botoes: { chave: Exclude<Aberta, null>; rotulo: string }[] }> = {
  consulta: {
    nome: 'Volta de consulta', regra: 'consulte cada pedido na Claro',
    destrava: 'Destrava: em cada pedido, Manter com a justificativa do que você viu na Claro, ou o botão da mudança (andar, defesa, cobrança ou cancelar).',
    botoes: [
      { chave: 'movel', rotulo: 'Input realizado Móvel' }, { chave: 'fixa', rotulo: 'Input realizado Fixa' },
      { chave: 'validacao', rotulo: 'Validação pendente' }, { chave: 'negociacao', rotulo: 'Negociação (erro de input)' },
      { chave: 'reprovado', rotulo: 'Crédito reprovado' }, { chave: 'manual', rotulo: 'Análise manual' },
    ],
  },
  erro_solar: {
    nome: 'Erro Solar', regra: '2 h sem refazer',
    destrava: 'Destrava: refazer o input (o pedido sai da fila), TT Recursos, cancelar, ou a pendência externa quando não depende de você (o pedido vai para o fim da fila e volta em 2 h). O Manter não destrava.',
    botoes: [{ chave: 'erro_solar', rotulo: 'Abrir a triagem do Erro Solar' }],
  },
  instalacao: {
    nome: 'Em instalação', regra: '2 h depois do fim do turno, sem resposta do cliente',
    destrava: 'Destrava: falar com o cliente na conversa do pedido, Instalado 100%, Técnico não veio, cancelar, ou o e-mail à gerente na TT Fixa.',
    botoes: [{ chave: 'instalacao', rotulo: 'Abrir a triagem Em instalação' }],
  },
  instalacao_volta: {
    nome: 'Em instalação', regra: '2 h depois da hora marcada',
    destrava: 'Destrava: Instalado · Finalizado 100%, Reagendar com a data nova ou cancelado.',
    botoes: [{ chave: 'instalacao', rotulo: 'Abrir a triagem Em instalação' }],
  },
  input: {
    nome: 'Input realizado', regra: '4 h sem consulta',
    destrava: 'Destrava: o pedido andar, ou Manter com a justificativa do que você viu na Claro.',
    botoes: [{ chave: 'movel', rotulo: 'Input realizado Móvel' }, { chave: 'fixa', rotulo: 'Input realizado Fixa' }],
  },
  validacao: {
    nome: 'Validação pendente', regra: '4 h sem consulta',
    destrava: 'Destrava: Aprovado, Análise ou cancelar, ou Manter com a justificativa do que você viu na Claro.',
    botoes: [{ chave: 'validacao', rotulo: 'Triagem Validação pendente' }],
  },
  negociacao: {
    nome: 'Negociação (erro de input)', regra: '4 h sem consulta',
    destrava: 'Destrava: o pedido andar, ou Manter com a justificativa do que você viu na Claro.',
    botoes: [{ chave: 'negociacao', rotulo: 'Triagem Erro de Input' }],
  },
  credito: {
    nome: 'Crédito', regra: '1 dia útil sem tratativa',
    destrava: 'Destrava: Aprovado CPC, cancelar, registrar a defesa ou o retorno, cobrar na Claro, ou Manter com a justificativa.',
    botoes: [{ chave: 'reprovado', rotulo: 'Crédito reprovado' }, { chave: 'manual', rotulo: 'Análise manual' }],
  },
};

const primeiroNome = (nome: string | null | undefined) => {
  const p = (nome ?? '').split(' ')[0] || '';
  return p.charAt(0) + p.slice(1).toLowerCase();
};

export function BkoTramitacaoTrava() {
  const { data: perfil } = useUserProfile();
  const ehTramitacao = FUNCOES.includes(perfil?.funcao ?? '');
  const [aberta, setAberta] = useState<Aberta>(null);
  const [pendencia, setPendencia] = useState(false);
  const { pathname } = useLocation();
  const q = useQuery({
    queryKey: ['bko-tramitacao-travado', perfil?.id],
    enabled: ehTramitacao,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Trava> => {
      const { data, error } = await rpcTriagem('fn_bko_tramitacao_travado', {});
      if (error) throw new Error(error.message || 'Não deu para conferir a trava da Tramitação.');
      return data as Trava;
    },
  });
  const fechar = () => { setAberta(null); void q.refetch(); };
  const triagens = <>
    <TriagemSolarModal isOpen={aberta === 'movel'} onClose={fechar} familia="MOVEL" />
    <TriagemSolarModal isOpen={aberta === 'fixa'} onClose={fechar} familia="FIXA" />
    <TriagemCotacaoModal isOpen={aberta === 'validacao'} onClose={fechar} />
    <TriagemDefesaCreditoModal isOpen={aberta === 'reprovado'} onClose={fechar} coluna="CREDITO REPROVADO" />
    <TriagemDefesaCreditoModal isOpen={aberta === 'manual'} onClose={fechar} coluna="ANALISE MANUAL CREDITO" />
    <TriagemEmInstalacaoModal isOpen={aberta === 'instalacao'} onClose={fechar} />
    <TriagemErroSolarModal isOpen={aberta === 'erro_solar'} onClose={fechar} />
    <TriagemFluxoModal config={TRIAGENS_FLUXO['NEGOCIAÇÃO (ERRO DE INPUT)']} isOpen={aberta === 'negociacao'} onClose={fechar} />
  </>;

  if (!ehTramitacao || !q.data?.travado || !q.data.fila) return triagens;
  const t = q.data;
  const fila = FILAS[t.fila];
  const pedidos = t.pedidos_trava ?? [];
  const faltam = pedidos.filter((p) => !p.batido).length;
  const ehErroSolar = t.fila === 'erro_solar';
  const ehConsulta = t.fila === 'consulta';
  const hora = t.horario ?? t.hora;
  const pausada = t.pausada ? FILAS[t.pausada] : null;
  const abrirDo = (p: PedidoTrava) => (p.coluna && TRIAGEM_DA_COLUNA[p.coluna]) || fila.botoes[0].chave;
  const titulo = ehConsulta
    ? (pedidos.length === 1 ? '1 pedido para consultar' : `${pedidos.length} pedidos para consultar`)
    : (pedidos.length === 1 ? '1 pedido passou do prazo' : `${pedidos.length} pedidos passaram do prazo`);

  if (pathname.startsWith('/whatsapp-inbox')) {
    return (
      <div role="status" className="fixed left-1/2 top-3 z-[9000] flex w-[min(94vw,720px)] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-4 py-2 text-white shadow-lg">
        <Lock className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1 text-[12.5px]"><span className="font-bold">Trava das {hora}:</span> {fila.nome} · faltam <span className="font-bold tabular-nums">{faltam}</span> de {pedidos.length}</p>
        <button type="button" onClick={() => setAberta(fila.botoes[0].chave)} className="rounded-md border border-white/40 px-2 py-1 text-[11.5px] font-bold hover:bg-white/10">Voltar à trava</button>
        {triagens}
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="trava-tramitacao-titulo"
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-background/95 p-4 backdrop-blur-[2px]">
      <div className="max-h-[94vh] w-full max-w-[560px] overflow-y-auto rounded-xl border border-border-warm bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]">
        <div className="bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-6 py-5 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-white/60">{primeiroNome(perfil?.nome)} · Tramitação · trava das {hora ?? '-'}</p>
          <h2 id="trava-tramitacao-titulo" className="mt-1 font-display text-[22px] font-bold">
            {titulo}
          </h2>
          <p className="mt-1 text-[12.5px] text-white/85">{fila.nome} · {fila.regra}. Trate {pedidos.length === 1 ? 'este pedido' : 'estes pedidos'} e a sua fila volta.</p>
          {pausada && <p className="mt-1 text-[12px] text-white/62">Ao terminar, você volta para a trava pausada: {pausada.nome}.</p>}
          <p className="mt-3 font-display text-[34px] font-bold leading-none tabular-nums">Faltam {faltam} de {pedidos.length}</p>
        </div>
        <div className="border-b border-border px-6 py-3">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{ehConsulta ? 'Pedidos da volta' : 'Pedidos atrasados'}</p>
            <p className="text-[11px] text-muted-foreground">Travas hoje: <span className="font-bold tabular-nums text-primary">{t.travas_hoje ?? 0}</span></p>
          </div>
          <div className="max-h-[240px] overflow-y-auto">
            {pedidos.map((p) => (
              <div key={p.pedido_id} className="grid h-8 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 border-t border-border text-[12.5px] first:border-t-0">
                <span className="min-w-0 truncate">
                  <span className="font-semibold tabular-nums text-foreground">{p.numero_pedido ?? '-'}</span>
                  <span className="text-muted-foreground"> · {p.cliente || '-'}</span>
                </span>
                {p.batido
                  ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success"><Check className="h-3 w-3" /> batido</span>
                  : ehConsulta
                    ? <span className="text-[11px] text-muted-foreground">consultar</span>
                    : <span className="text-[11px] font-bold tabular-nums text-danger">{rotuloPrazo(-(p.estourou_min ?? 0))}</span>}
                {!p.batido
                  ? <button type="button" onClick={() => setAberta(abrirDo(p))}
                      className="rounded-md border border-border px-2 py-0.5 text-[11px] font-semibold text-foreground hover:border-primary/50 hover:text-primary">Abrir</button>
                  : <span />}
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3 px-6 py-4">
          <div className={cn('grid gap-3', fila.botoes.length > 1 || ehErroSolar ? 'grid-cols-2' : 'grid-cols-1')}>
            {fila.botoes.map((b, i) => (
              <button key={b.chave} type="button" onClick={() => setAberta(b.chave)}
                className={i === 0
                  ? 'flex h-11 items-center justify-center gap-2 rounded-lg bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] px-3 text-[13px] font-bold text-white transition hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]'
                  : 'flex h-11 items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 text-[13px] font-bold text-foreground transition-colors hover:border-primary/50 hover:text-primary'}>
                {b.rotulo}
              </button>
            ))}
            {ehErroSolar && (
              <button type="button" onClick={() => setPendencia(true)}
                className="flex h-11 items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 text-[13px] font-bold text-foreground transition-colors hover:border-primary/50 hover:text-primary">
                Pendência externa
              </button>
            )}
          </div>
          <p className="text-[12px] text-muted-foreground">{fila.destrava} O resto do CRM fecha até tratar estes; o Inbox continua aberto.</p>
        </div>
        {(t.agenda?.length ?? 0) > 0 && (
          <details className="border-t border-border px-6 py-3">
            <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Os horários de hoje</summary>
            <AgendaTramitacao agenda={t.agenda ?? []} className="mt-2" />
          </details>
        )}
      </div>
      {triagens}
      {ehErroSolar && pendencia && <PendenciaDialog pedidos={pedidos.filter((p) => !p.batido)}
        onClose={() => setPendencia(false)} onFeito={() => { setPendencia(false); void q.refetch(); }} />}
    </div>
  );
}

function PendenciaDialog({ pedidos, onClose, onFeito }: {
  pedidos: PedidoTrava[]; onClose: () => void; onFeito: () => void;
}) {
  const [pedidoId, setPedidoId] = useState<string | null>(() => pedidos[0]?.pedido_id ?? null);
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const salvar = usePendenciaExterna(onFeito);
  const letras = texto.toLowerCase().replace(/[^a-z0-9à-ü]/g, '').length;
  const pode = !!pedidoId && !!motivo && letras >= 15 && !salvar.isPending;
  const confirmar = async () => {
    if (!pedidoId) return;
    try {
      await salvar.mutateAsync({ pedidoId, motivo, texto: texto.trim() });
      toast.success('Pendência registrada. O prazo do pedido recomeça agora.');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Não foi possível registrar a pendência.'); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !salvar.isPending) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Pendência externa</DialogTitle>
          <DialogDescription>Por que este pedido não depende de você agora? O prazo dele recomeça e a supervisora vê o motivo no placar.</DialogDescription>
        </DialogHeader>
        {pedidos.length === 0
          ? <p className="text-[12.5px] text-muted-foreground">Nenhum pedido da trava está faltando.</p>
          : <>
            <Label>Pedido</Label>
            <div className="flex flex-wrap gap-1.5">
              {pedidos.map((p) => (
                <button key={p.pedido_id} type="button" onClick={() => setPedidoId(p.pedido_id)} aria-pressed={pedidoId === p.pedido_id}
                  className={cn('rounded-md border px-2.5 py-1 text-xs tabular-nums', pedidoId === p.pedido_id ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border')}>
                  {p.numero_pedido ?? '-'} · {(p.cliente || '-').slice(0, 24)}
                </button>
              ))}
            </div>
            <Label>Motivo</Label>
            <div className="flex flex-wrap gap-1.5">
              {MOTIVOS_PENDENCIA.map((o) => (
                <button key={o.codigo} type="button" onClick={() => setMotivo(o.codigo)} aria-pressed={motivo === o.codigo}
                  className={cn('rounded-md border px-2.5 py-1 text-xs', motivo === o.codigo ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border')}>
                  {o.rotulo}
                </button>
              ))}
            </div>
            <Label htmlFor="pendencia-texto">O que falta e com quem (obrigatório, 15 letras)</Label>
            <Textarea id="pendencia-texto" value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} disabled={salvar.isPending}
              placeholder="Ex.: cliente precisa mandar o contrato social atualizado; pedi pela conversa às 10:12" />
          </>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={salvar.isPending} onClick={onClose}>Cancelar</Button>
          <Button disabled={!pode} onClick={() => void confirmar()}>Registrar a pendência</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
